import React, { useState, useEffect } from "react";
import { Editor as EditorRef } from "@tinymce/tinymce-react";
import { handleSSMLValueClick } from "shared/utils/tts-editor-helper";
import { getCurrentSSMLValue } from "features/TTS/TTSCustomEditor/utils/editorKeyDownHandler";
import { SSMLTag, SSMLTagsColors, timeOptions } from "shared/constants";
import PresetCustomMenu from "features/TTS/TTSCustomEditor/components/PresetCustomMenu";
import MenuWrapper from "../../components/MenuWrapper";

interface DurationMenuProps {
  editorRef: React.RefObject<EditorRef | null>;
  onDurationChange?: (value: string) => void;
  onApply?: (value: string) => void;
}

export default function DurationMenu({
  editorRef,
  onDurationChange,
  onApply,
}: DurationMenuProps): JSX.Element {
  const [timeUnit, setTimeUnit] = useState<string>("ms");
  const [durationValue, setDurationValue] = useState<string>("100");

  // Parse current value when component mounts or editor changes
  useEffect(() => {
    if (!editorRef.current?.editor) return;

    const currentValue = getCurrentSSMLValue(editorRef, SSMLTag.Duration);
    if (currentValue) {
      let numericPart = "100";
      let unitPart = "ms";

      if (currentValue.endsWith("ms")) {
        numericPart = currentValue.substring(0, currentValue.length - 2);
        unitPart = "ms";
      } else if (currentValue.endsWith("s") && !currentValue.endsWith("ms")) {
        numericPart = currentValue.substring(0, currentValue.length - 1);
        unitPart = "s";
      }

      if (!isNaN(Number(numericPart))) {
        setDurationValue(numericPart);
        setTimeUnit(unitPart);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorRef.current]);

  const handleDurationValueChange = (value: string) => {
    setDurationValue(value);
    if (onDurationChange) {
      const durationValueWithUnit = `${value}${timeUnit}`;
      onDurationChange(durationValueWithUnit);
    }
  };

  const handleTimeUnitChange = (unit: string) => {
    let newDurationValue = durationValue;

    if (durationValue === "1" && timeUnit === "s" && unit === "ms") {
      newDurationValue = "100";
    } else if (durationValue === "100" && timeUnit === "ms" && unit === "s") {
      newDurationValue = "1";
    }

    setDurationValue(newDurationValue);
    setTimeUnit(unit);

    if (onDurationChange) {
      const durationValueWithUnit = `${newDurationValue}${unit}`;
      onDurationChange(durationValueWithUnit);
    }
  };

  const applyDuration = () => {
    if (
      !editorRef.current ||
      durationValue.trim() === "" ||
      Number(durationValue) < 0
    )
      return;

    const durationValueWithUnit = `${durationValue}${timeUnit}`;

    handleSSMLValueClick(
      editorRef,
      SSMLTag.Duration,
      durationValueWithUnit,
      SSMLTagsColors.ProsodyTag,
      []
    );

    if (onApply) {
      onApply(durationValueWithUnit);
    }
  };

  return (
    <MenuWrapper
      id="duration-menu-wrapper"
      showDeleteButton={false}
      editorRef={editorRef}
      tagType={SSMLTag.Duration}
      onApply={applyDuration}
    >
      <PresetCustomMenu
        textFieldWidth="292px"
        showPresetMenuList={false}
        showCustomTextField
        handleClick={handleDurationValueChange}
        unitOptions={timeOptions}
        selectedUnit={timeUnit}
        setSelectedUnit={handleTimeUnitChange}
        allowMathSymbols={false}
        defaultValue={durationValue}
        value={durationValue}
      />
    </MenuWrapper>
  );
}
